import Component from "@base/Component.js";
import config from "@/config.js";
import router from "@/services/router.js";
import t from "@/services/translationService.js";
import { renderInto, hasContent } from "@/services/richText.js";
import GallerySlideshow from "@modules/gallery/components/GallerySlideshow.js";

/**
 * Gear detail page — the replacement for the old ItemPopup overlay.
 *
 * Top to bottom: the image/text diptych (same diagonal split the gallery detail
 * uses for story/map), the long-form detail body, then every linked image as a
 * thumbnail grid that opens the shared slideshow.
 *
 * props: { item: ShowcaseObject, lang }
 */
export default class GearDetail extends Component {
  /** Where this item's files are served from — see showcaseRoutes.js. */
  #imageDir() {
    return `showcase/${encodeURIComponent(this.props.item.gearItemId)}`;
  }

  render() {
    const { item, lang } = this.props;
    const titleImage = item.titleImage();
    const blurb = item.content(lang);
    const hasText = Boolean(blurb && String(blurb).trim());

    const textPanel = `
      <div class="diptych__panel diptych__text">
        <div class="diptych__inner">
          <span class="diptych__label">${t.t("showcase.detailLabel")}</span>
          <h1 class="diptych__title">${Component.escape(item.title(lang))}</h1>
          <div class="diptych__text-body">${Component.escape(blurb)}</div>
        </div>
      </div>`;
    const imagePanel = `
      <div class="diptych__panel diptych__image">
        <span class="diptych__label diptych__label--image">${t.t("showcase.image")}</span>
        <img class="diptych__image-img" src="${config.imageBase}/${this.#imageDir()}/${encodeURIComponent(titleImage || "")}"
             alt="${Component.escape(item.title(lang))}" />
      </div>`;

    // Same three-way fallback the gallery detail uses: both → diptych, one →
    // that panel full width, neither → just the heading below.
    let head = "";
    if (hasText && titleImage) head = `<div class="diptych">${textPanel}${imagePanel}</div>`;
    else if (titleImage) head = `<div class="diptych diptych--single">${imagePanel}</div>`;
    else if (hasText) head = `<div class="diptych diptych--single diptych--text">${textPanel}</div>`;
    else head = `<header class="gear-detail__head"><h1>${Component.escape(item.title(lang))}</h1></header>`;

    // The body is filled in onMounted via renderInto — it is HTML, and the one
    // place in this app that renders markup it did not build (see richText.js).
    const detail = hasContent(item.detail(lang))
      ? `<section class="gear-detail__body rt-content"></section>`
      : "";

    const thumbs = item
      .imageNames()
      .map(
        (name, i) => `
        <button class="thumb" data-index="${i}">
          <img src="${config.imageBase}/${this.#imageDir()}/${encodeURIComponent(name)}?size=thumb"
               alt="${Component.escape(item.title(lang))} ${i + 1}" loading="lazy" />
        </button>`
      )
      .join("");
    const gallery = thumbs
      ? `<section class="gear-detail__images">
           <h2 class="gear-detail__images-title">${t.t("showcase.linkedImages")}</h2>
           <div class="thumb-grid">${thumbs}</div>
         </section>`
      : "";

    return `
      <article class="gear-detail">
        <a class="back-link" href="#/gear">&#8249; ${t.t("showcase.back")}</a>
        ${head}
        ${detail}
        ${gallery}
      </article>`;
  }

  onMounted() {
    const { item, lang } = this.props;

    renderInto(this.$(".gear-detail__body"), item.detail(lang));

    const names = item.imageNames();
    this.$$(".thumb").forEach((btn) => {
      this.listen(btn, "click", () => {
        new GallerySlideshow({
          // The gear namespace is a two-segment path, so it goes in as a base
          // path — passing it as a galleryId would percent-encode the slash.
          imageDir: this.#imageDir(),
          images: names.map((name) => ({ name })),
          startIndex: Number(btn.getAttribute("data-index")) || 0,
          alt: item.title(lang),
        }).mount(document.body);
      });
    });

    this.listen(this.$(".back-link"), "click", (e) => {
      e.preventDefault();
      router.go("/gear");
    });
  }
}
